// Navigation island - menu functionality
// Import from shared GSAP module for better code splitting
import { gsap, Power2 } from '../gsap-config.js';
import { initServicesMegaMenu } from '../components/services-mega-menu.js';

let navigationInitialized = false;

export function initNavigation() {
    if (navigationInitialized) return;
    navigationInitialized = true;

    // Header pattern scroll handler
    const headerPattern = document.querySelector('.header-pattern');
    if (headerPattern) {
        let ticking = false;
        const updateHeaderPattern = () => {
            const scrolled = window.scrollY;
            const opacity = Math.min(scrolled / 100, 1);
            headerPattern.style.opacity = opacity > 0 ? opacity : 0;
            ticking = false;
        };
        
        window.addEventListener('scroll', function() {
            if (!ticking) {
                requestAnimationFrame(updateHeaderPattern);
                ticking = true;
            }
        }, { passive: true });
    }

    // Main menu hover
    document.querySelectorAll(".tt-main-menu-list").forEach(menuList => {
        menuList.addEventListener("mouseenter", function () {
            this.classList.add("tt-mm-hover");
        });
        menuList.addEventListener("mouseleave", function () {
            this.classList.remove("tt-mm-hover");
        });
    });

    initServicesMegaMenu();

    // Mobile menu toggle - clean implementation
    const menuToggle = document.getElementById("tt-m-menu-toggle-btn-wrap");
    if (!menuToggle) return;

    class MobileMenu {
        constructor(toggleElement) {
            this.toggle = toggleElement;
            this.isOpen = false;
            this.isAnimating = false;
            this.currentTimeline = null;
            this.debounceTimer = null;
            
            this.init();
        }

        // Get selectors dynamically to ensure they're available
        getSelectors() {
            return {
                menu: document.querySelector('.tt-main-menu'),
                menuItems: document.querySelectorAll('.tt-main-menu-content > ul > li'),
                socialItems: document.querySelectorAll('.tt-main-menu-inner > .social-buttons ul > li')
            };
        }

        init() {
            // Prevent default anchor behavior
            const anchor = this.toggle.querySelector('a');
            if (anchor) {
                anchor.addEventListener('click', (e) => e.preventDefault());
            }

            // Single click handler
            this.toggle.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.handleToggle();
            });

            // Setup menu link handlers (only once)
            this.setupMenuLinks();
        }

        setupMenuLinks() {
            document.querySelectorAll(".tt-main-menu a, .tt-logo a").forEach(link => {
                if (!link.matches('[target="_blank"], [href^="#"], [href^="mailto"], [href^="tel"]')) {
                    link.addEventListener('click', () => {
                        if (document.documentElement.classList.contains('tt-view-transitions')) {
                            return;
                        }
                        if (!this.isOpen) {
                            return;
                        }

                        const { menuItems } = this.getSelectors();
                        gsap.set("#content-wrap", { autoAlpha: 0 });
                        if (menuItems.length) {
                            gsap.to(menuItems, {
                                duration: 0.4,
                                y: -80,
                                autoAlpha: 0,
                                stagger: 0.05,
                                ease: Power2.easeIn
                            });
                        }
                    });
                }
            });
        }

        handleToggle() {
            // Debounce rapid clicks
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }

            this.debounceTimer = setTimeout(() => {
                if (this.isAnimating) return;
                
                this.isOpen = !this.isOpen;
                this.isAnimating = true;

                if (this.isOpen) {
                    this.open();
                } else {
                    this.close();
                }
            }, 50); // Very short debounce to prevent double-clicks
        }

        open() {
            // Kill any running animation
            this.killAnimation();

            const { menu, menuItems, socialItems } = this.getSelectors();
            if (!menu || !menuItems.length) return;

            // Update DOM state
            document.documentElement.classList.add("tt-no-scroll");
            document.body.classList.add("tt-m-menu-open", "tt-m-menu-active", "tt-m-menu-toggle-no-click");

            // Show menu container first
            gsap.set(menu, { autoAlpha: 1 });

            // Reset element states for clean animation (set initial state)
            gsap.set(menuItems, { y: 80, autoAlpha: 0 });
            if (socialItems.length) {
                gsap.set(socialItems, { y: 80, autoAlpha: 0 });
            }

            // Create and run animation - animate TO visible state
            this.currentTimeline = gsap.timeline({
                onComplete: () => {
                    document.body.classList.remove("tt-m-menu-toggle-no-click");
                    this.isAnimating = false;
                    this.currentTimeline = null;
                }
            });

            // Animate menu items TO visible (from y:80, autoAlpha:0 to y:0, autoAlpha:1)
            this.currentTimeline.to(menuItems, { 
                duration: 0.4, 
                y: 0, 
                autoAlpha: 1, 
                stagger: 0.05, 
                ease: Power2.easeOut 
            });
            
            // Animate social items TO visible
            if (socialItems.length) {
                this.currentTimeline.to(socialItems, { 
                    duration: 0.4, 
                    y: 0, 
                    autoAlpha: 1, 
                    stagger: 0.05, 
                    ease: Power2.easeOut 
                }, "-=0.2");
            }

            import('../icons.js').then(({ processIconsInContainer }) => {
                const menuSocial = document.querySelector('.tt-main-menu-inner > .social-buttons');
                if (menuSocial) {
                    processIconsInContainer(menuSocial);
                }
            }).catch(() => {});
        }

        close() {
            // Kill any running animation
            this.killAnimation();

            const { menu, menuItems, socialItems } = this.getSelectors();
            if (!menu) return;

            // Update DOM state
            document.documentElement.classList.remove("tt-no-scroll");
            document.body.classList.remove("tt-m-menu-open");

            // Create and run animation
            this.currentTimeline = gsap.timeline({
                onComplete: () => {
                    // Cleanup after animation
                    if (menuItems.length) {
                        gsap.set(menuItems, { clearProps: "all" });
                    }
                    if (socialItems.length) {
                        gsap.set(socialItems, { clearProps: "all" });
                    }
                    gsap.set(menu, { clearProps: "all" });
                    
                    // Reset submenus
                    document.querySelectorAll(".tt-submenu").forEach(submenu => {
                        submenu.style.display = 'none';
                    });
                    document.querySelectorAll(".tt-submenu-trigger").forEach(trigger => {
                        trigger.classList.remove("tt-m-submenu-open");
                    });
                    
                    document.body.classList.remove("tt-m-menu-active");
                    this.isAnimating = false;
                    this.currentTimeline = null;
                }
            });

            if (menuItems.length) {
                this.currentTimeline.to(menuItems, { 
                    duration: 0.4, 
                    y: -80, 
                    autoAlpha: 0, 
                    stagger: 0.05, 
                    ease: Power2.easeIn 
                });
            }
            
            if (socialItems.length) {
                this.currentTimeline.to(socialItems, { 
                    duration: 0.4, 
                    y: -80, 
                    autoAlpha: 0, 
                    stagger: 0.05, 
                    ease: Power2.easeIn 
                }, "-=0.2");
            }
            
            this.currentTimeline.to(menu, { 
                duration: 0.4, 
                autoAlpha: 0 
            }, "-=0.2");

            // Allow reopening quickly (clear animation flag early)
            setTimeout(() => {
                if (!this.isOpen) {
                    this.isAnimating = false;
                }
            }, 200);
        }

        killAnimation() {
            if (this.currentTimeline) {
                this.currentTimeline.kill();
                this.currentTimeline = null;
            }
        }
    }

    // Initialize mobile menu
    new MobileMenu(menuToggle);

    // Append element if link href contains #
    document.querySelectorAll('.tt-submenu-trigger > a[href^="#"]').forEach(link => {
        const span = document.createElement('span');
        span.classList.add('tt-submenu-trigger-m');
        link.parentElement.appendChild(span);
    });

    // Open submenu if link href contains #
    document.querySelectorAll('.tt-submenu-trigger > a[href^="#"]').forEach(link => {
        link.nextElementSibling.addEventListener("click", function () {
            const parent = this.parentElement;
            if (parent.classList.contains("tt-m-submenu-open")) {
                parent.classList.remove("tt-m-submenu-open");
                parent.nextElementSibling.style.display = 'none';
            } else {
                parent.parentElement.querySelectorAll(".tt-submenu").forEach(submenu => submenu.style.display = 'none');
                parent.parentElement.querySelectorAll(".tt-submenu-trigger").forEach(trigger => trigger.classList.remove("tt-m-submenu-open"));
                parent.classList.toggle("tt-m-submenu-open");
                parent.nextElementSibling.style.display = 'block';
            }
        });
    });

    // Open submenu on caret click
    document.querySelectorAll(".tt-submenu-trigger").forEach(trigger => {
        const span = document.createElement('span');
        span.classList.add('tt-m-caret');
        trigger.appendChild(span);
    });

    document.querySelectorAll(".tt-m-caret").forEach(caret => {
        caret.addEventListener("click", function () {
            const parent = this.parentElement;
            if (parent.classList.contains("tt-m-submenu-open")) {
                parent.classList.remove("tt-m-submenu-open");
                parent.nextElementSibling.style.display = 'none';
            } else {
                parent.parentElement.querySelectorAll(".tt-submenu").forEach(submenu => submenu.style.display = 'none');
                parent.parentElement.querySelectorAll(".tt-submenu-trigger").forEach(trigger => trigger.classList.remove("tt-m-submenu-open"));
                parent.classList.toggle("tt-m-submenu-open");
                parent.nextElementSibling.style.display = 'block';
            }
        });
    });
}

// Auto-initialize when script loads (island will handle timing)
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNavigation);
} else {
    initNavigation();
}
